import React, {useState} from 'react'
import {Button, DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import {useDispatch, useSelector} from 'react-redux';
import {hasValue} from "@amsanalytics/react-shared-library";
import {Error} from "@progress/kendo-react-labels";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import styles from './_customer-number-reservation-dialog.module.scss';
import {uniq} from "ramda";
import { DialogProps } from '../DialogProps';
import {
  ApplicationDispatch,
  SET_SERVER_MESSAGE,
  updateAmsCustomerNumberSuccess
} from "../../../store/application/types";
import { AppState } from '../../../store/rootReducer';
import { IConfiguration } from '../../../Data/ConfigurationData';
import { IPostAmsCustomer, IPreviousAmsCustomerDetailWithNames } from "../../../Interfaces/Common/IPostAmsLoan";
import { CustomerNumberInputsDialog } from "../CustomerNumberInputs";
import { AmsCustomerPreCheckSearchResult } from "../../../Interfaces/Common/AmsCustomerPreCheckSearchResult";
import {
  getAmsCustomersForAmsCustomerNumbersAsync,
  getAtLeastOneCustomerNumberIsTooLongError,
  getDefaultCustomerDetailsForPosting,
  getLosCustomerIdAndAmsCustomerNumberPairs,
  maxAmsCustomerNumberLength
} from "../../Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingUtils";

interface CustomerNumberReservationDialogProps extends DialogProps<void> {
  configuration: IConfiguration;
}

export const CustomerNumberReservationDialog: React.FC<CustomerNumberReservationDialogProps> = (props) => {
  const amsLoanPostingDialogDefaultCodes = props.configuration.amsLoanPostingDialogDefaultCodes;
  const dispatch = useDispatch<ApplicationDispatch>();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const [loading, setLoading] = useState<boolean>(false);
  const [customerDetails, setCustomerDetails] = useState<IPostAmsCustomer[]>(getDefaultCustomerDetailsForPosting(currentApp, amsLoanPostingDialogDefaultCodes?.customerNumberCreationMethod));
  
  const trimAndTruncateString = (inputString: string | undefined, length: number) => {
    return inputString?.trim().slice(0, length);
  }

  const [preCheckSearchResults, setPreCheckSearchResults] = useState<AmsCustomerPreCheckSearchResult[]>([]);

  const lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer = (selectedPreviousCustomerFromAms: IPreviousAmsCustomerDetailWithNames, lastName: string): boolean => {
    return !!selectedPreviousCustomerFromAms && !selectedPreviousCustomerFromAms.optionForManualEntry && (lastName.toUpperCase().trim() !== selectedPreviousCustomerFromAms.lastName.toUpperCase().trim());
  }

  const desiredAmsCustomerNumbers = customerDetails.map(acd => acd.amsCustomerNumber?.trim().toUpperCase()) ?? [];
  const anAmsOrLosCustomerAlreadyExistsForADesiredNumber = preCheckSearchResults.some(ac =>
    desiredAmsCustomerNumbers.includes(ac.queriedAmsCustomerNumber.toUpperCase().trim()) && hasValue(ac.existingAmsCustomerString) || hasValue(ac.existingLosCustomerString));
  
  const atLeastOneCustomerDoesntHaveNumberEntered = customerDetails.some(c => !hasValue(c.amsCustomerNumber));
  const atLeastOneCustomerDoesntHaveAcknowledgedNameDifference = customerDetails.some(c =>
    lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer(c.selectedPreviousCustomerFromAms, c.lastName) && !c.confirmSelectedNameIsDifferentThanApplicant) ?? false;
  // If the same value exists more than once, the unique number of customer numbers will be less than the number of customers
  const thereAreMultipleCustomersWithTheSameAmsCustomerNumber = customerDetails.length > uniq(customerDetails.map(c => c.amsCustomerNumber)).length;

  const errors: string[] = [];
  
  if (anAmsOrLosCustomerAlreadyExistsForADesiredNumber) {
    errors.push("See error listed below applicant");
  }
  if (atLeastOneCustomerDoesntHaveNumberEntered) {
    errors.push("One of the applicants doesn't have a desired AMS Customer # yet");
  }
  if (atLeastOneCustomerDoesntHaveAcknowledgedNameDifference) {
    errors.push("Please acknowledge the name difference between the selected previous AMS customer and the LOS applicant");
  }
  const thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage = "Each customer number should be unique";
  if (thereAreMultipleCustomersWithTheSameAmsCustomerNumber) {
    errors.push(thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage);
  }
  
  const atLeastOneCustomerNumberIsTooLongError = getAtLeastOneCustomerNumberIsTooLongError(customerDetails, maxAmsCustomerNumberLength);
  if (hasValue(atLeastOneCustomerNumberIsTooLongError)) {
    errors.push(atLeastOneCustomerNumberIsTooLongError!);
  }

  // Fires whenever user enters a number into the input field.
  const handleDesiredCustomerNumberUpdate = (customerId: string, value: string): void => {
    setCustomerDetails(customerDetails.map(c => {
      if (c.customerId === customerId) {
        return {
          ...c,
          amsCustomerNumber: trimAndTruncateString(value, maxAmsCustomerNumberLength) ?? ''
        }
      } else {
        return c;
      }
    }));
  };

  const handleSelectCustomerNumberFromAms = (customerId: string, selectedAmsCustomerDetail: IPreviousAmsCustomerDetailWithNames): void => {
    setCustomerDetails(customerDetails.map(c => {
      if (c.customerId === customerId) {
        const newCustomerDetail = {
          ...c,
          selectedPreviousCustomerFromAms: selectedAmsCustomerDetail,
          amsCustomerNumber: trimAndTruncateString(selectedAmsCustomerDetail.amsCustomerNumber, maxAmsCustomerNumberLength) ?? ''
        };

        if (lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer(selectedAmsCustomerDetail, c.lastName)) {
          newCustomerDetail.confirmSelectedNameIsDifferentThanApplicant = false;
        }

        return newCustomerDetail;
      } else {
        return c;
      }
    }));
  };

  const handleNameDifferenceAcknowledgement = (customerId: string, value: boolean): void => {
    setCustomerDetails(customerDetails.map(c => {
      if (c.customerId === customerId) {
        return {
          ...c,
          confirmSelectedNameIsDifferentThanApplicant: value,
        }
      } else {
        return c;
      }
    }));
  };
  
  // User has clicked the OK button.
  const makePreChecksAndSetPostLoanDetailsBeforeClosing = async () => {
    setLoading(true);

    const losCustomerIdAndAmsCustomerNumberPairs = getLosCustomerIdAndAmsCustomerNumberPairs(customerDetails);
    const amsCustomerPreCheckSearchResult = await getAmsCustomersForAmsCustomerNumbersAsync(losCustomerIdAndAmsCustomerNumberPairs, true, dispatch);
    const customerNumbersThatAlreadyHaveACustomer = amsCustomerPreCheckSearchResult.filter(ac => hasValue(ac.existingAmsCustomerString) || hasValue(ac.existingLosCustomerString));
    setPreCheckSearchResults(customerNumbersThatAlreadyHaveACustomer);

    if (customerNumbersThatAlreadyHaveACustomer.length > 0) {
      setLoading(false);
      return;
    }
    
    dispatch(updateAmsCustomerNumberSuccess(losCustomerIdAndAmsCustomerNumberPairs));
    dispatch({ type: SET_SERVER_MESSAGE, payload: { message: "Customer number(s) set successfully", isError: false } });

    props.accept && props.accept();
  };
  
  return (
    <div className={styles.dialogContainer}>
      <DialogTitle id="form-dialog-title">{props.title}</DialogTitle>
      <DialogContent>
        <div style={{display: "flex", width: "350px", marginRight: "10px"}}>
          <CustomerNumberInputsDialog 
            customers={customerDetails}
            preCheckSearchResults={preCheckSearchResults}
            handleNameDifferenceAcknowledgement={handleNameDifferenceAcknowledgement}
            handleDesiredCustomerNumberUpdate={handleDesiredCustomerNumberUpdate}
            handleSelectCustomerNumberFromAms={handleSelectCustomerNumberFromAms}
          />
        </div>

      </DialogContent>
      <DialogActions>
        <div style={{marginRight: "350px", width: "350px", display: "flex", flexDirection: "column", marginTop: "10px"}}>
          {errors.map((error, key) => <Error key={key}>{error}</Error>)}
        </div>
        <Button
          onClick={async () => await makePreChecksAndSetPostLoanDetailsBeforeClosing()} 
          color="primary"
          variant={"contained"}
          disabled={thereAreMultipleCustomersWithTheSameAmsCustomerNumber 
            || atLeastOneCustomerDoesntHaveNumberEntered
            || anAmsOrLosCustomerAlreadyExistsForADesiredNumber
            || errors.length > 0
        }
        >
          Save
        </Button>
        <Button 
          onClick={() => props.closeDialog()}
          variant={"contained"}
          color="secondary"
        >
          Cancel
        </Button>
      </DialogActions>
      <Backdrop open={loading}>
        <CircularProgress color="inherit"/>
      </Backdrop>
    </div>
  )
}